import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FiArrowUp } from 'react-icons/fi';
import { RiRobot2Line, RiCloseLine } from 'react-icons/ri';

import './DBHChatbot.css';

const quickActions = [
  {
    label: 'Explore Services',
    prompt: 'Give me a concise overview of all services offered by Data Bolta Hai.',
  },
  {
    label: 'Meet Our Team',
    prompt: 'Tell me about the team and experts at Data Bolta Hai using only information available on the website.',
  },
  {
    label: 'About Data Bolta Hai',
    prompt: 'Give me a concise overview of Data Bolta Hai, including what the company does, its mission and vision, using only website information.',
  },
  {
    label: 'Our Solutions',
    prompt: 'Give me a concise overview of the solutions offered by Data Bolta Hai.',
  },
  {
    label: 'Our Products',
    prompt: 'Tell me about the products offered by Data Bolta Hai. Include DBH Insight and Deepti Art.',
  },
  {
    label: 'Contact Us',
    prompt: 'Give me the contact information available on the Data Bolta Hai website.',
  },
];

const nudgeMessages = [
  'Hey! Need help?',
  "I'm here for you!",
  'Ask me anything!',
  'Got questions?',
  "Let's talk data!",
  "Hi there! 👋",
  'Curious about us?',
  'Need GST help?',
  "Let's connect!",
  'Data insights await!',
];

const renderFormattedMessage = (content) => {
  if (!content) return null;
  const lines = content.split('\n');
  const elements = [];
  let currentList = [];

  const formatInlineText = (text) => {
    const parts = text.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, idx) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={idx}>{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  lines.forEach((line, index) => {
    const trimmed = line.trim();
    if (trimmed.startsWith('- ') || trimmed.startsWith('* ') || trimmed.startsWith('• ')) {
      const bulletText = trimmed.replace(/^[-*•]\s*/, '');
      currentList.push(<li key={`li-${index}`}>{formatInlineText(bulletText)}</li>);
    } else {
      if (currentList.length > 0) {
        elements.push(<ul key={`ul-${index}`}>{currentList}</ul>);
        currentList = [];
      }
      if (trimmed.length > 0) {
        elements.push(<p key={`p-${index}`}>{formatInlineText(trimmed)}</p>);
      }
    }
  });

  if (currentList.length > 0) {
    elements.push(<ul key="ul-end">{currentList}</ul>);
  }

  return elements.length > 0 ? elements : <p>{formatInlineText(content)}</p>;
};

const ChatbotWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const chatRef = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fabRef = useRef(null);
  const [nudge, setNudge] = useState('');
  const nudgeTimerRef = useRef(null);
  const nudgeIndexRef = useRef(0);

  const [position, setPosition] = useState({ x: null, y: null });
  const [isDragging, setIsDragging] = useState(false);
  const didDragRef = useRef(false);
  const dragStartRef = useRef(null);
  const posRef = useRef(position);
  posRef.current = position;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading]);

  useEffect(() => {
    if (!isOpen) return;
    const handleOutsidePointer = (e) => {
      if (chatRef.current && !chatRef.current.contains(e.target) && fabRef.current && !fabRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('pointerdown', handleOutsidePointer);
    return () => document.removeEventListener('pointerdown', handleOutsidePointer);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setNudge('');
      if (nudgeTimerRef.current) clearInterval(nudgeTimerRef.current);
      return;
    }

    const showNudge = () => {
      const msg = nudgeMessages[nudgeIndexRef.current % nudgeMessages.length];
      nudgeIndexRef.current += 1;
      setNudge(msg);
      setTimeout(() => setNudge(''), 3500);
    };

    const firstDelay = setTimeout(() => {
      showNudge();
      nudgeTimerRef.current = setInterval(showNudge, 12000);
    }, 5000);

    return () => {
      clearTimeout(firstDelay);
      if (nudgeTimerRef.current) clearInterval(nudgeTimerRef.current);
    };
  }, [isOpen]);

  const clampToViewport = useCallback((x, y) => {
    const el = fabRef.current;
    if (!el) return { x, y };
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    return {
      x: Math.max(8, Math.min(x, window.innerWidth - w - 8)),
      y: Math.max(8, Math.min(y, window.innerHeight - h - 8)),
    };
  }, []);

  const onPointerDown = useCallback((e) => {
    if (e.button !== 0) return;
    const el = fabRef.current;
    if (!el) return;
    el.setPointerCapture(e.pointerId);
    const rect = el.getBoundingClientRect();
    dragStartRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      elLeft: rect.left,
      elTop: rect.top,
    };
    setIsDragging(true);
    didDragRef.current = false;
  }, []);

  const onPointerMove = useCallback((e) => {
    const ds = dragStartRef.current;
    if (!ds || ds.pointerId !== e.pointerId) return;
    const dx = e.clientX - ds.startX;
    const dy = e.clientY - ds.startY;
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
      didDragRef.current = true;
    }
    const newX = ds.elLeft + dx;
    const newY = ds.elTop + dy;
    setPosition(clampToViewport(newX, newY));
  }, [clampToViewport]);

  const onPointerUp = useCallback((e) => {
    const ds = dragStartRef.current;
    if (!ds || ds.pointerId !== e.pointerId) return;
    dragStartRef.current = null;
    setIsDragging(false);
    fabRef.current?.releasePointerCapture(e.pointerId);

    if (!didDragRef.current) {
      setIsOpen((prev) => !prev);
    }
  }, []);

  const sendText = async (text) => {
    const updatedMessages = [...messages, { text, sender: 'user' }];
    setMessages(updatedMessages);
    setIsLoading(true);

    const history = updatedMessages.map((msg) => ({
      role: msg.sender === 'user' ? 'user' : 'assistant',
      content: msg.text,
    }));

    let reply = '';

    try {
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text, history }),
      });
      if (response.ok) {
        const data = await response.json();
        if (data.response && !data.response.startsWith('Server configuration error')) {
          reply = data.response;
        }
      }
    } catch (e) {
      console.log('Backend endpoint offline, connecting via direct AI fallback.');
    }

    if (!reply) {
      reply = "I'm having trouble connecting to my AI service right now. Please try again in a moment.";
    }

    if (!reply) {
      reply = "Hi! I'm DBH-AI. How can I help you with Data Bolta Hai services, GST reconciliation, or company inquiries today?";
    }

    setMessages((prev) => [...prev, { text: reply, sender: 'bot' }]);
    setIsLoading(false);
  };

  const handleSendMessage = () => {
    const text = inputValue.trim();
    if (!text || isLoading) return;
    setInputValue('');
    sendText(text);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleQuickAction = (action) => {
    sendText(action.prompt);
  };

  const fabStyle = {};
  if (position.x !== null) {
    fabStyle.left = position.x;
    fabStyle.top = position.y;
    fabStyle.right = 'auto';
    fabStyle.bottom = 'auto';
  }

  return (
    <>
      {/* Floating Robot FAB */}
      <div
        ref={fabRef}
        className={`dbh-robot-fab ${isOpen ? 'is-open' : ''} ${isDragging ? 'is-dragging' : ''}`}
        style={fabStyle}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        role="button"
        tabIndex={0}
        aria-label="Open DBH-AI Assistant"
      >
        <img
          src="/bot-img.png"
          alt="DBH-AI Robot"
          className="dbh-robot-img"
          draggable={false}
        />
        {nudge && <span className="dbh-nudge-bubble">{nudge}</span>}
        <span className="dbh-robot-label">Ask anything</span>
        <div className="dbh-robot-glow" />
      </div>

      {/* Chatbot Panel */}
      <div
        className={`chatbot-container ${isOpen ? 'open' : ''}`}
        ref={chatRef}
        role="dialog"
        aria-label="DBH-AI Assistant"
      >
        {/* Header */}
        <div className="chatbot-header">
          <div className="chatbot-header-left">
            <span className="chatbot-header-icon"><RiRobot2Line /></span>
            <div className="chatbot-header-text">
              <h3>DBH-AI</h3>
              <span className="chatbot-status">
                <span className="status-dot" /> Your Data Intelligence Assistant
              </span>
            </div>
          </div>
          <button
            className="chatbot-close"
            onClick={() => setIsOpen(false)}
            aria-label="Close DBH-AI Assistant"
          >
            <RiCloseLine />
          </button>
        </div>

        {/* Messages */}
        <div className="chatbot-content">
          <div className="chatbot-messages">
            {messages.length === 0 ? (
              <div className="welcome-message">
                <img src="/bot-img.png" alt="DBH-AI" className="welcome-bot-img" />
                <strong className="welcome-heading">Hi! I'm DBH-AI</strong>
                <p className="welcome-sub">Your intelligent assistant for Data Bolta Hai.</p>
                <div className="welcome-suggestions">
                  {quickActions.map((action) => (
                    <button
                      key={action.label}
                      className="suggestion-chip"
                      onClick={() => handleQuickAction(action)}
                    >
                      {action.label}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((msg, index) => (
                <div key={index} className={`message ${msg.sender}`}>
                  {msg.sender === 'bot' && (
                    <span className="msg-avatar">
                      <RiRobot2Line />
                    </span>
                  )}
                  <div className="msg-bubble">
                    {msg.sender === 'bot' ? renderFormattedMessage(msg.text) : msg.text}
                  </div>
                </div>
              ))
            )}

            {isLoading && (
              <div className="message bot">
                <span className="msg-avatar">
                  <RiRobot2Line />
                </span>
                <div className="msg-bubble typing-indicator">
                  Thinking
                  <span className="typing-dots"><i /><i /><i /></span>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="chatbot-input-area">
            <div className="chatbot-input-wrapper">
              <textarea
                ref={inputRef}
                rows={1}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask anything about Data Bolta Hai..."
                aria-label="Type your message"
                disabled={isLoading}
              />
              <button
                className="chatbot-send"
                onClick={handleSendMessage}
                disabled={!inputValue.trim() || isLoading}
                aria-label="Send message"
              >
                <FiArrowUp />
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ChatbotWidget;
